import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  TenantStatus,
  TenantAgentConfigSetting,
  UpdateInternalTenantAgentModelsRequest,
} from '@resolve-ai/shared';
import { tenantsApi } from '../lib/tenants-api';
import { ApiError } from '../lib/api-client';

export type TenantDetailTab = 'overview' | 'users' | 'projects' | 'models' | 'settings';

interface ToastState {
  isOpen: boolean;
  message: string;
  variant: 'success' | 'error' | 'warning' | 'info';
}

const EMPTY_TOAST: ToastState = { isOpen: false, message: '', variant: 'info' };

// Stable fallback so consumers don't see a new array on every render while loading
const NO_AGENT_MODELS: TenantAgentConfigSetting[] = [];

export function useTenantDetailPage(tenantId: string, activeTab: TenantDetailTab) {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<ToastState>(EMPTY_TOAST);

  const overviewQuery = useQuery({
    queryKey: ['tenant', tenantId, 'overview'],
    queryFn: () => tenantsApi.getOverview(tenantId),
  });

  const usersQuery = useQuery({
    queryKey: ['tenant', tenantId, 'users'],
    queryFn: () => tenantsApi.getUsers(tenantId),
    enabled: activeTab === 'users',
  });

  const projectsQuery = useQuery({
    queryKey: ['tenant', tenantId, 'projects'],
    queryFn: () => tenantsApi.getProjects(tenantId),
    enabled: activeTab === 'projects',
  });

  const agentModelsQuery = useQuery({
    queryKey: ['tenant', tenantId, 'agent-models'],
    queryFn: () => tenantsApi.getAgentModels(tenantId),
    enabled: activeTab === 'models',
  });

  const showToast = (message: string, variant: ToastState['variant']) =>
    setToast({ isOpen: true, message, variant });

  const invalidateTenant = () => {
    queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    queryClient.invalidateQueries({ queryKey: ['tenants'] });
  };

  const updateMutation = useMutation({
    mutationFn: (payload: { name?: string; status?: TenantStatus }) =>
      tenantsApi.update(tenantId, payload),
    onSuccess: () => {
      invalidateTenant();
      showToast('Tenant updated successfully', 'success');
    },
    onError: (error: unknown) => {
      showToast(error instanceof ApiError ? error.message : 'Failed to update tenant', 'error');
    },
  });

  const agentModelsMutation = useMutation({
    mutationFn: (models: UpdateInternalTenantAgentModelsRequest['models']) =>
      tenantsApi.updateAgentModels(tenantId, { models }),
    onSuccess: (data) => {
      queryClient.setQueryData(['tenant', tenantId, 'agent-models'], data);
      showToast('AI models updated. Changes apply to new tasks.', 'success');
    },
    onError: (error: unknown) => {
      showToast(error instanceof ApiError ? error.message : 'Failed to update AI models', 'error');
    },
  });

  return {
    overview: overviewQuery.data?.overview,
    isOverviewLoading: overviewQuery.isLoading,
    hasOverviewError: overviewQuery.isError,
    users: usersQuery.data?.users ?? [],
    isUsersLoading: usersQuery.isLoading,
    projects: projectsQuery.data?.projects ?? [],
    isProjectsLoading: projectsQuery.isLoading,
    agentModels: agentModelsQuery.data?.agentModels ?? NO_AGENT_MODELS,
    isAgentModelsLoading: agentModelsQuery.isLoading,
    hasAgentModelsError: agentModelsQuery.isError,
    updateMutation,
    agentModelsMutation,
    toast: { ...toast, close: () => setToast(EMPTY_TOAST) },
  };
}
